import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { MedicalRecordRequestDocumentType } from "@/src/models/constants/document-types";
import { ImagingType } from "@/src/models/constants/imaging-types";
import { BODY_PART_PROMPT, SCHEDULING_PRIORITY_PROMPT } from "@/src/models/constants/prompt";
import { RECORD_REQUEST_SOURCE_PROMPT, RecordRequestSource } from "@/src/models/constants/record-request-source";
import { Specialty } from "@/src/models/constants/specialty";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { diagnosisListSchema, SchedulingPriority } from "@/src/models/documents/referral";
import { locationSchema } from "@/src/models/location";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export enum SubpoenaPurpose {
  APPEARANCE = "Appearance",
  RECORDS = "Records",
  NONE = "None",
}

export enum LegalStatute {
  FLORIDA_STATUTE_766 = "Florida Statute 766",
  FLORIDA_STATUTE_627 = "Florida Statute 627",
  OTHER = "Other",
}

export enum RequestingLocation {
  CHILDNET = "ChildNet",
  CHILD_SERVICES = "Child Services",
  EXTERNAL_REQUESTOR = "External Requestor",
  INSURANCE_PAYER = "Insurance/Payer",
  SPECIALIST = "Specialist",
  OTHER = "Other",
  VERISMA = "Verisma",
}

const extendedMedicalRecordRequestSchema = z.object({
  isAdw: isAdwSchema,
  ...DocumentDateSchema.shape,
  schedulingPriority: z.nativeEnum(SchedulingPriority).nullable().describe(SCHEDULING_PRIORITY_PROMPT),
  provider: practitionerSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  location: locationSchema.nullable(),
  receivingLocation: locationSchema.nullable(),
  referringLocation: locationSchema.nullable(),
  imagingType: z.nativeEnum(ImagingType).nullable(),
  containsCptCode: z.boolean().nullable(),
  isLegal: z
    .boolean()
    .nullable()
    .describe("True when the medical records request is legal in nature; false otherwise."),
  legalStatute: z
    .nativeEnum(LegalStatute)
    .nullable()
    .describe(
      "Legal statute cited in the request. Florida-specific values are listed; choose Other when the document cites a different statute (including bare statute numbers without 'Florida') or when no statute is cited."
    ),
  transferReason: z
    .string()
    .nullable()
    .describe(
      "Free-text reason the patient is transferring out of the practice (e.g. 'aged out', 'moved out', or the documented reason). Used to populate the Global Alert note."
    ),
  incompleteRoi: z
    .string()
    .nullable()
    .describe(
      "Free-text describing which required release-of-information elements are missing on the form. Populated only when the form contains a required field that is blank; leave null when the missing element is not part of the form."
    ),
  lwcImmunization: z
    .boolean()
    .nullable()
    .describe("True when the document is a request for Last Well Check (LWC) records and/or immunization records."),
  requestingLocation: z.nativeEnum(RequestingLocation).nullable(),
  specialty: z.nativeEnum(Specialty).nullable(),
  subpoenaPurpose: z.nativeEnum(SubpoenaPurpose).nullable(),
  isFromMedicalExaminer: z
    .boolean()
    .nullable()
    .describe(
      "True only when the medical record request is sent from a Medical Examiner or Coroner's office (for example, the requesting party, letterhead, fax header, or signature identifies a Medical Examiner, Coroner, or Office of the Medical Examiner). False or null otherwise."
    ),
  dmeCompanyName: z.string().nullable(),
  companyName: z.string().nullable(),
  containsMultiplePatients: z.boolean().nullable(),
  signatureRequired: z.boolean().nullable(),
  recordRequestSource: z.nativeEnum(RecordRequestSource).nullable().describe(RECORD_REQUEST_SOURCE_PROMPT),
  bodyPart: z.string().nullable().describe(BODY_PART_PROMPT),
  diagnosisList: diagnosisListSchema.nullable(),
});

export const medicalRecordRequestTypeDisplay: Record<MedicalRecordRequestDocumentType, string> = {
  [MedicalRecordRequestDocumentType.DOH]: "Medical Record Request - DOH",
  [MedicalRecordRequestDocumentType.IMAGING_CD]: "Medical Record Request - Imaging CD",
  [MedicalRecordRequestDocumentType.LEGAL]: "Medical Record Request - Legal",
  [MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST]: "Medical Records Request",
  [MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST_HEDIS]: "Medical Record Request - HEDIS",
  [MedicalRecordRequestDocumentType.OTHER]: "Medical Record Request - Other",
  [MedicalRecordRequestDocumentType.RESPONSE]: "Response to Medical Records Request",
  [MedicalRecordRequestDocumentType.SSA]: "Medical Record Request - SSA",
};

const baseMedicalRecordRequestFields = {
  patient: patientSchema,
};

const medicalRecordRequestCustomFields: Record<MedicalRecordRequestDocumentType, Record<string, z.ZodTypeAny>> = {
  [MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST_HEDIS]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.DOH]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.IMAGING_CD]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.LEGAL]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.OTHER]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.RESPONSE]: extendedMedicalRecordRequestSchema.shape,
  [MedicalRecordRequestDocumentType.SSA]: extendedMedicalRecordRequestSchema.shape,
};

const makeMedicalRecordRequestSchema = (
  type: MedicalRecordRequestDocumentType,
  customFields: Record<string, z.ZodTypeAny>
) =>
  z.object({
    type: z.literal(type),
    ...baseMedicalRecordRequestFields,
    ...customFields,
  });

export const medicalRecordRequestSchemas: Record<
  MedicalRecordRequestDocumentType,
  ReturnType<typeof makeMedicalRecordRequestSchema>
> = (Object.values(MedicalRecordRequestDocumentType) as MedicalRecordRequestDocumentType[]).reduce(
  (acc, type) => {
    acc[type] = makeMedicalRecordRequestSchema(type, medicalRecordRequestCustomFields[type]);

    return acc;
  },
  {} as Record<MedicalRecordRequestDocumentType, ReturnType<typeof makeMedicalRecordRequestSchema>>
);

const medicalRecordRequestSchemasArray = (
  Object.values(MedicalRecordRequestDocumentType) as MedicalRecordRequestDocumentType[]
).map((type) => medicalRecordRequestSchemas[type]) as [ReturnType<typeof makeMedicalRecordRequestSchema>];

export const anyMedicalRecordRequestSchema = z.discriminatedUnion("type", medicalRecordRequestSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type MedicalRecordRequest = ExtractedDocumentFields<
  MedicalRecordRequestDocumentType,
  typeof extendedMedicalRecordRequestSchema,
  typeof baseMedicalRecordRequestFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const medicalRecordRequestShape: MedicalRecordRequest = {
  id: "",
  type: MedicalRecordRequestDocumentType.MEDICAL_RECORD_REQUEST,
  patient: null,
  receivingLocation: null,
  referringLocation: null,
  dateReceived: null,
  isLegal: null,
  legalStatute: null,
  transferReason: null,
  incompleteRoi: null,
  lwcImmunization: null,
  requestingLocation: null,
  specialty: null,
  subpoenaPurpose: null,
  isFromMedicalExaminer: null,
  dmeCompanyName: null,
  companyName: null,
  containsMultiplePatients: null,
  signatureRequired: null,
  recordRequestSource: null,
  bodyPart: null,
  diagnosisList: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
